import { useCallback, useEffect, useMemo, useRef } from "react";
import { getRealtimeAxisEndMs } from "../utils/realtimeAxis";
import type { BpsLogPoint } from "../utils/types";

// リアルタイム表示の右端時刻(軸終端)を算出し、データ停止時は現在時刻側へ切り替える。
export const useRealtimeAxisEnd = (args: {
  isRealtime: boolean;
  bpslogData: BpsLogPoint[];
  realtimeNowMs: number;
  realtimeRightEdgeFallbackLagMs: number;
  stickyRealtimeAxisEndMsRef: React.MutableRefObject<number | null>;
  stallAfterMs?: number;
}) => {
  const {
    isRealtime,
    bpslogData,
    realtimeNowMs,
    realtimeRightEdgeFallbackLagMs,
    stickyRealtimeAxisEndMsRef,
    stallAfterMs = 3000,
  } = args;

  const lastRealtimeDataEndMsRef = useRef<number | null>(null);
  const lastRealtimeDataUpdatedAtMsRef = useRef<number>(Date.now());
  const lastRealtimeDataKeyRef = useRef<string | null>(null);

  const resetRealtimeAxisTracking = useCallback(() => {
    stickyRealtimeAxisEndMsRef.current = null;
    lastRealtimeDataEndMsRef.current = null;
    lastRealtimeDataUpdatedAtMsRef.current = Date.now();
    lastRealtimeDataKeyRef.current = null;
  }, [stickyRealtimeAxisEndMsRef]);

  const realtimeAxisEndInfo = useMemo(() => {
    if (!isRealtime) return null;
    return getRealtimeAxisEndMs(
      bpslogData,
      realtimeNowMs,
      realtimeRightEdgeFallbackLagMs,
      stickyRealtimeAxisEndMsRef.current,
    );
  }, [
    isRealtime,
    bpslogData,
    realtimeNowMs,
    realtimeRightEdgeFallbackLagMs,
    stickyRealtimeAxisEndMsRef,
  ]);

  const lastRealtimeDataKey = useMemo(() => {
    if (!isRealtime) return null;
    const lastPt = bpslogData.length ? bpslogData[bpslogData.length - 1] : null;
    return lastPt && typeof (lastPt as any)?.modem_id === "number"
      ? `id:${(lastPt as any).modem_id}`
      : typeof (lastPt as any)?.timestamp === "string"
        ? `ts:${(lastPt as any).timestamp}`
        : null;
  }, [isRealtime, bpslogData]);

  useEffect(() => {
    if (!isRealtime) return;
    if (!realtimeAxisEndInfo) return;
    if (realtimeAxisEndInfo.usedData) {
      stickyRealtimeAxisEndMsRef.current = realtimeAxisEndInfo.endMs;

      // 最新点のID/時刻キーで「データが動いている」ことを判定する（endMsは秒丸めのため誤判定し得る）。
      if (
        lastRealtimeDataKey &&
        lastRealtimeDataKeyRef.current !== lastRealtimeDataKey
      ) {
        lastRealtimeDataKeyRef.current = lastRealtimeDataKey;
        lastRealtimeDataUpdatedAtMsRef.current = Date.now();
      }

      // 右端算出のフォールバック用に直近endMsを保持する。
      if (lastRealtimeDataEndMsRef.current !== realtimeAxisEndInfo.endMs) {
        lastRealtimeDataEndMsRef.current = realtimeAxisEndInfo.endMs;
      }
    }
  }, [
    isRealtime,
    lastRealtimeDataKey,
    realtimeAxisEndInfo?.endMs,
    realtimeAxisEndInfo?.usedData,
    stickyRealtimeAxisEndMsRef,
  ]);

  const isRealtimeDataStalled = useMemo(() => {
    if (!isRealtime) return false;
    if (lastRealtimeDataEndMsRef.current === null) return false;
    const elapsed =
      realtimeNowMs - (lastRealtimeDataUpdatedAtMsRef.current ?? 0);
    return Number.isFinite(elapsed) ? elapsed >= stallAfterMs : false;
  }, [isRealtime, realtimeNowMs, stallAfterMs]);

  const realtimeAxisEndMs = isRealtime
    ? isRealtimeDataStalled
      ? Math.floor(realtimeNowMs / 1000) * 1000
      : (realtimeAxisEndInfo?.endMs ?? null)
    : null;

  return {
    realtimeAxisEndInfo,
    realtimeAxisEndMs,
    isRealtimeDataStalled,
    resetRealtimeAxisTracking,
  };
};
